// Payment success page component
// - Displayed after successful Stripe checkout
// - Extracts session_id from URL params
// - Calls the verify-payment Edge Function on each poll, which:
//     (a) confirms payment status directly with Stripe, and
//     (b) activates the license in the DB if the webhook hasn't fired yet
// - This makes verification reliable even when webhooks are slow (common in local dev)
// - Once verified, updates the TanStack Query cache and redirects to Dashboard

import { useRouter } from "@tanstack/react-router";
import { CheckCircle, Loader2 } from "lucide-react";
import { useEffect, useState } from "react";
import { useLicense } from "../hooks/useLicense";
import { supabase } from "../lib/supabase";

export default function PaymentSuccess() {
  const router = useRouter();
  const [isVerifying, setIsVerifying] = useState(true);
  const [timedOut, setTimedOut] = useState(false);
  const { refetch } = useLicense();

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const sessionId = params.get("session_id");

    if (!sessionId) {
      void router.navigate({ to: "/inventory" });
      return;
    }

    console.log("💳 Payment success! Session ID:", sessionId);
    console.log("⏳ Starting payment verification...");

    let attempts = 0;
    const maxAttempts = 20;
    let cancelled = false;

    const poll = async () => {
      if (cancelled) return;

      attempts++;
      console.log(`🔄 verify-payment attempt ${attempts}/${maxAttempts}`);

      try {
        // Call the verify-payment Edge Function rather than passively polling
        // the DB. The function talks to Stripe directly to confirm the payment,
        // then writes has_web_license = true itself if the webhook hasn't done
        // it yet. This eliminates the dependency on timely webhook delivery.
        const { data, error } = await supabase.functions.invoke(
          "verify-payment",
          { body: { sessionId } },
        );

        if (error) {
          // Log but treat as transient — Stripe may still be processing
          console.warn(
            `🟡 verify-payment returned an error (attempt ${attempts}):`,
            error.message,
          );
        }

        if (data?.success === true) {
          console.log(
            "🟢 License verified and active — refreshing cache and redirecting",
          );
          // Push the confirmed license status into the TanStack Query cache so
          // Dashboard renders the correct state immediately on load, without
          // needing a second round-trip to the DB.
          await refetch();
          if (!cancelled) {
            setIsVerifying(false);
            setTimeout(() => {
              void router.navigate({ to: "/inventory" });
            }, 1500);
          }
          return;
        }

        // Payment not confirmed yet — retry
        if (attempts < maxAttempts) {
          setTimeout(poll, 1000);
        } else {
          console.error(
            `🔴 Payment verification timed out after ${maxAttempts} attempts`,
          );
          if (!cancelled) {
            setTimedOut(true);
            setIsVerifying(false);
          }
        }
      } catch (err) {
        console.error("🔴 Unexpected error during payment verification:", err);
        if (attempts < maxAttempts) {
          setTimeout(poll, 1000);
        } else {
          if (!cancelled) {
            setTimedOut(true);
            setIsVerifying(false);
          }
        }
      }
    };

    // Give the webhook a moment before the first attempt, in case it fires
    // quickly and saves us a round-trip to verify-payment
    const timer = setTimeout(poll, 1000);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [refetch, router]);

  return (
    <div className="bg-gradient-animated flex min-h-screen flex-col items-center justify-center p-4">
      <div className="w-full max-w-md rounded-lg bg-white p-8 text-center shadow-lg dark:bg-gray-800">
        {isVerifying ? (
          <>
            <Loader2 className="mx-auto mb-4 h-16 w-16 animate-spin text-blue-600 dark:text-blue-400" />
            <h1 className="mb-2 text-2xl font-bold text-gray-900 dark:text-white">
              Verifying Payment...
            </h1>
            <p className="text-gray-600 dark:text-gray-400">
              Please wait while we confirm your purchase
            </p>
          </>
        ) : timedOut ? (
          <>
            <h1 className="mb-2 text-2xl font-bold text-gray-900 dark:text-white">
              Payment Received
            </h1>
            <p className="mb-6 text-gray-600 dark:text-gray-400">
              Your payment was processed, but license activation is taking
              longer than expected. Please go to the dashboard and try creating
              your household — it should be ready shortly.
            </p>
            <button
              type="button"
              onClick={() => {
                void router.navigate({ to: "/inventory" });
              }}
              className="w-full rounded-lg bg-blue-600 py-3 font-medium text-white transition-colors hover:bg-blue-700"
            >
              Go to Dashboard
            </button>
          </>
        ) : (
          <>
            <CheckCircle className="mx-auto mb-4 h-16 w-16 text-green-600" />
            <h1 className="mb-2 text-2xl font-bold text-gray-900 dark:text-white">
              Payment Successful!
            </h1>
            <p className="text-gray-600 dark:text-gray-400">
              Your license is active. Redirecting you now...
            </p>
          </>
        )}
      </div>
    </div>
  );
}
